<template>
  <span class="search-summary" v-if="dataSource.length">
    <a-tag
      v-for="item in dataSource"
      :key="item.field_name"
      color="green"
      closable
      @close="handleClose(item.field_name)"
    >
      {{ item.summary }}
    </a-tag>
    <a-button
      size="small"
      type="text"
      @click="handleRefresh"
      v-if="showRefresh"
    >
      <RedoOutlined />
    </a-button>
  </span>
</template>

<script setup lang="ts">
import { isEmpty } from 'liyu-pc-base/utils/function';
import moment from 'moment';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SearchConditionItem, SearchConfig, SearchType } from './typing';
import { getDefaultSearchOperator, operator_type_map, transferRemoteSelectProValue } from './utils';
interface Props {
  searchConfig: SearchConfig[];
  searchConditions: SearchConditionItem[];
  eventBus: any;
  searchType: SearchType;
  showRefresh?: boolean;
  /** 表头自定义搜索字段配置（query_field_search_config_url 查询结果），用于在摘要中展示未在 search_config 中配置的字段条件 */
  fieldSearchConfig?: SearchConfig[];
}
const props = withDefaults(defineProps<Props>(), {
  searchType: 'advanced',
  showRefresh: true,
});
const emit = defineEmits(['update:searchConditions', 'change']);

const { t } = useI18n();
const dataSource = ref<any[]>([]);
const transferValue = async (configItem, operator, value) => {
  const str_list: any = [];
  let transfer_value;
  if (operator === 'is_or_not_blank_or_null' || operator === 'is_or_not_null') {
    if (value === 'yes') {
      return t('is_or_not_blank_or_null.yes');
    } else {
      return t('is_or_not_blank_or_null.no');
    }
  } else if (operator === '_eq') {
    if (['上月', '本月', '今日', '昨日', '期初'].includes(value[0])) {
      return t(value[0]);
    }
  } else if (configItem.options instanceof Array) {
    if (value instanceof Array) {
      value.forEach(item => {
        configItem.options.forEach(i => {
          if (item == i.value) {
            str_list.push(i.label || i.name);
          }
        });
      });
      if (props.searchType === 'advanced') {
        return `${operator_type_map[operator]} ${str_list.join(',')}`;
      } else {
        return str_list.join(',');
      }
    } else {
      configItem.options.forEach(i => {
        if (value == i.value) {
          transfer_value = i.label;
        }
      });
      if (props.searchType === 'advanced') {
        return `${operator_type_map[operator]} ${transfer_value}`;
      } else {
        return transfer_value;
      }
    }
  } else if (configItem.type == 'monthpicker' && configItem.dateFormat) {
    if (value instanceof Array) {
      const str_list = [];
      value.forEach(i => {
        if (i) {
          str_list.push(moment(i).format(configItem.dateFormat));
        } else {
          str_list.push(i);
        }
      });
      transfer_value = str_list.join(',');
    } else {
      transfer_value = moment(value).format(configItem.dateFormat);
    }
    if (props.searchType === 'advanced') {
      return `${operator_type_map[operator]} ${transfer_value}`;
    } else {
      return transfer_value;
    }
  } else if (configItem.type === 'remoteselectpro') {
    const transfer_value = await transferRemoteSelectProValue(value, {
      postUrl: configItem.postUrl,
      valueKey: configItem.valueKey,
      labelKey: configItem.labelKey,
      labelKeyList: configItem.labelKeyList,
      labelKeyType: configItem.labelKeyType,
      labelSpace: configItem.labelSpace,
      queryType: configItem.queryType,
    });
    if (props.searchType === 'advanced') {
      return `${operator_type_map[operator]} ${transfer_value}`;
    } else {
      return transfer_value;
    }
  } else {
    if (value instanceof Array) {
      value = value.map(item => {
        if (item instanceof Boolean) {
          return item ? '是' : '否';
        } else {
          return item;
        }
      });
      value = value.join(',');
    } else {
      if (value instanceof Boolean) {
        value = value ? '是' : '否';
      }
    }
    if (props.searchType === 'advanced') {
      return `${operator_type_map[operator]} ${value}`;
    } else {
      return value;
    }
  }
};
const getDataSource = async () => {
  const searchConditionsMap = {};
  for (const condition of props.searchConditions) {
    searchConditionsMap[condition.field_name] = {
      value: condition.value,
      operator: condition.operator,
    };
  }
  // 合并 searchConfig 与 fieldSearchConfig（表头自定义搜索的远程配置），
  // 本地 searchConfig 优先，覆盖远程同名配置；按 field_name 去重
  const mergedConfigMap: Record<string, SearchConfig> = {};
  for (const configItem of [...(props.fieldSearchConfig || []), ...(props.searchConfig || [])]) {
    if (configItem?.field_name) {
      mergedConfigMap[configItem.field_name] = configItem;
    }
  }
  let tmpValue: any = Object.values(mergedConfigMap).map(async configItem => {
    const data = searchConditionsMap[configItem.field_name] || {
      operator: getDefaultSearchOperator(configItem),
      value: null,
    };
    let summary = null;
    if (
      data.value instanceof Array &&
      data.value.length > 0 &&
      !data.value.some(item => isEmpty(item))
    ) {
      const transfer_value = await transferValue(configItem, data.operator, data.value);
      const groupPrefix = configItem.group_name ? `${configItem.group_name} - ` : '';
      summary = `${groupPrefix}${configItem.label}: ${transfer_value}`;
    }
    return {
      field_name: configItem.field_name,
      summary,
    };
  });
  tmpValue = await Promise.all(tmpValue);
  dataSource.value = tmpValue.filter(item => item.summary);
};
const handleClose = (fieldName: string) => {
  // 与 SearchForm 保持一致：同步 emit，避免 nextTick 时序/异常中断导致 change 丢失
  emit(
    'update:searchConditions',
    props.searchConditions.filter(item => item.field_name !== fieldName),
  );
  emit('change');
  props.eventBus?.emit('refresh');
};
const handleRefresh = () => {
  props.eventBus.emit('refresh');
};
onMounted(() => {
  getDataSource();
});
watch(() => props.searchConditions, getDataSource);
watch(() => props.searchConfig, getDataSource);
watch(() => props.fieldSearchConfig, getDataSource);
watch(() => props.searchType, getDataSource);
</script>
<style lang="less" scoped>
.search-summary {
  display: inline-block;
  padding: 4px 2px;
}
</style>
